"use client";

import { ChevronDownIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/utils";
import { WebViewPopover } from "../Pages/Diary/SubHeader/WebViewPopover";
import { Calendar } from "../ui/calendar";
import { EDSButton } from "./EDSButton";

interface EDSDatePickerProps {
  date?: Date;
  onSelect?: (date: Date | undefined) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  weekdayFormat?: "short" | "long";
  /** Accessible name for the date picker trigger button (e.g. "From" or "To") */
  ariaLabel?: string;
}

export function EDSDatePicker({
  date,
  onSelect,
  placeholder = "Select date",
  label,
  disabled = false,
  className,
  id,
  weekdayFormat = "short",
  ariaLabel
}: Readonly<EDSDatePickerProps>) {
  const [open, setOpen] = React.useState(false);
  const labelId = id ? `${id}-label` : undefined;

  const handleSelect = (selectedDate: Date | undefined) => {
    onSelect?.(selectedDate);
    setOpen(false);
  };

  const currentYear = new Date().getFullYear();
  const startMonth = new Date(currentYear - 5, 0);
  const endMonth = new Date(currentYear + 5, 11);

  return (
    <div className={cn("flex flex-col gap-3 w-fit", className)}>
      {label && (
        <label id={labelId} className="px-1 text-sm font-medium">
          {label}
        </label>
      )}
      <WebViewPopover
        open={open}
        onOpenChange={setOpen}
        side="bottom"
        sideOffset={5}
        content={
          <div
            className="w-auto overflow-hidden p-0"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Calendar
              mode="single"
              selected={date}
              captionLayout="dropdown"
              onSelect={handleSelect}
              startMonth={startMonth}
              endMonth={endMonth}
            />
          </div>
        }
      >
        <EDSButton
          variant="outline"
          className={cn(
            "flex items-center justify-between w-full px-3 py-2 text-sm",
            className
          )}
          id={id}
          disabled={disabled}
          data-eds-date-picker="true"
          aria-label={ariaLabel}
          aria-labelledby={labelId}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className="truncate text-left font-medium flex-1 min-w-0 mr-2">
            {date
              ? new Intl.DateTimeFormat("en-US", {
                  weekday: weekdayFormat,
                  month: "short",
                  day: "numeric"
                }).format(date)
              : placeholder}
          </span>
          <ChevronDownIcon className="h-4 w-4 opacity-50 flex-shrink-0" />
        </EDSButton>
      </WebViewPopover>
    </div>
  );
}
